import type { InputMessage, Modifier, MouseButton } from '@remote/shared';

import { enterFullscreen, exitFullscreen } from './fullscreen.js';

/**
 * Captura de ratón/teclado (desktop) y de toque (móvil), traducida al protocolo
 * de input.
 *
 * Dos modos, elegidos por capacidad del dispositivo:
 *
 * - **Desktop (Pointer Lock):** al capturar el puntero el cursor local desaparece y
 *   los movimientos siguen llegando aunque el ratón salga del vídeo — así se puede
 *   llevar el cursor remoto hasta los bordes. Keyboard Lock (en fullscreen)
 *   redirige Cmd+W y Cmd+Tab a la página. La posición se acumula de los deltas
 *   porque bajo lock clientX/Y se congela.
 *
 * - **Móvil (trackpad táctil):** Pointer Lock no existe en móvil, y el modelo
 *   "tocar = posición absoluta" es malo: el dedo tapa el punto y, con el vídeo en
 *   `object-fit: contain`, las franjas negras descuadran el mapeo. En su lugar se
 *   usa un modelo tipo trackpad de portátil: arrastrar mueve el cursor de forma
 *   RELATIVA (ves dónde está y no lo tapas), un toque corto es clic, mantener
 *   pulsado es clic derecho, y dos dedos desplazan. El teclado usa un input oculto
 *   y el mensaje `text` (inyección Unicode).
 *
 * Las coordenadas salen normalizadas 0–1; el agente las escala a su pantalla.
 */

interface InputCaptureOptions {
  video: HTMLVideoElement;
  /** Contenedor del vídeo, para la pantalla completa. */
  videoWrap: HTMLElement;
  /** Input oculto para el teclado software en móvil. */
  keyboardInput: HTMLInputElement;
  send: (message: InputMessage) => void;
  log: (line: string) => void;
  /** Se dispara cuando el control se activa o se suelta. */
  onActiveChange: (active: boolean) => void;
}

/** ¿Este dispositivo puede hacer Pointer Lock de forma útil? */
export function supportsPointerLock(): boolean {
  const coarse = window.matchMedia('(pointer: coarse)').matches;
  return 'requestPointerLock' in HTMLElement.prototype && !coarse;
}

/** Multiplicador de velocidad del trackpad: cruzar un escritorio grande con poco
 *  recorrido de dedo en una pantalla pequeña. */
const TRACKPAD_SPEED = 1.8;
/** Umbral en px para distinguir un toque (clic) de un arrastre (mover). */
const TAP_MOVE_THRESHOLD = 10;
/** Duración máxima de un toque para contar como clic. */
const TAP_MAX_MS = 300;
/** Tiempo de pulsación mantenida para el clic derecho. */
const LONG_PRESS_MS = 500;

function toMouseButton(button: number): MouseButton | null {
  return button === 0 || button === 1 || button === 2 ? button : null;
}

function modifiersOf(event: KeyboardEvent): Modifier[] {
  const mods: Modifier[] = [];
  if (event.metaKey) mods.push('cmd');
  if (event.shiftKey) mods.push('shift');
  if (event.altKey) mods.push('alt');
  if (event.ctrlKey) mods.push('ctrl');
  return mods;
}

export interface InputCapture {
  engage(): Promise<void>;
  release(): void;
  readonly touchMode: boolean;
}

interface KeyboardLockCapable {
  lock?: (keyCodes?: string[]) => Promise<void>;
  unlock?: () => void;
}

export function createInputCapture(options: InputCaptureOptions): InputCapture {
  const { video, videoWrap, keyboardInput, send, log, onActiveChange } = options;
  const touchMode = !supportsPointerLock();

  let normX = 0.5;
  let normY = 0.5;
  let touchActive = false;

  const clamp = (v: number): number => Math.min(1, Math.max(0, v));

  // ---- Desktop: pointer lock ------------------------------------------------

  const onMouseMove = (event: MouseEvent): void => {
    if (document.pointerLockElement !== video) return;
    const rect = video.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    normX = clamp(normX + event.movementX / rect.width);
    normY = clamp(normY + event.movementY / rect.height);
    send({ t: 'move', x: normX, y: normY });
  };

  const onMouseDown = (event: MouseEvent): void => {
    if (document.pointerLockElement !== video) return;
    const button = toMouseButton(event.button);
    if (button === null) return;
    event.preventDefault();
    send({ t: 'down', b: button, x: normX, y: normY });
  };

  const onMouseUp = (event: MouseEvent): void => {
    if (document.pointerLockElement !== video) return;
    const button = toMouseButton(event.button);
    if (button === null) return;
    event.preventDefault();
    send({ t: 'up', b: button, x: normX, y: normY });
  };

  const onWheel = (event: WheelEvent): void => {
    if (document.pointerLockElement !== video) return;
    event.preventDefault();
    send({ t: 'wheel', dx: event.deltaX, dy: event.deltaY });
  };

  const onContextMenu = (event: MouseEvent): void => {
    if (document.pointerLockElement === video || touchActive) event.preventDefault();
  };

  const onKeyDown = (event: KeyboardEvent): void => {
    if (document.pointerLockElement !== video) return;
    event.preventDefault();
    send({ t: 'key', code: event.code, down: true, mods: modifiersOf(event) });
  };

  const onKeyUp = (event: KeyboardEvent): void => {
    if (document.pointerLockElement !== video) return;
    event.preventDefault();
    send({ t: 'key', code: event.code, down: false, mods: modifiersOf(event) });
  };

  const onPointerLockChange = (): void => {
    const locked = document.pointerLockElement === video;
    onActiveChange(locked);
    if (!locked) log('control liberado (Esc). Toca "Tomar control" para retomarlo.');
  };

  const onVideoClick = (): void => {
    if (!touchMode && document.pointerLockElement !== video) {
      void video.requestPointerLock();
    }
  };

  // ---- Móvil: trackpad táctil ----------------------------------------------

  let activeTouchId: number | null = null;
  let lastClient: { x: number; y: number } | null = null;
  let startClient: { x: number; y: number; t: number } | null = null;
  let moved = false;
  let longPressTimer: number | null = null;
  let twoFingerLastY: number | null = null;

  const clearLongPress = (): void => {
    if (longPressTimer !== null) {
      window.clearTimeout(longPressTimer);
      longPressTimer = null;
    }
  };

  const midpointY = (touches: TouchList): number | null => {
    const a = touches[0];
    const b = touches[1];
    return a !== undefined && b !== undefined ? (a.clientY + b.clientY) / 2 : null;
  };

  const onTouchStart = (event: TouchEvent): void => {
    if (!touchActive) return;
    event.preventDefault();

    if (event.touches.length >= 2) {
      // Dos dedos: gesto de scroll. Anula cualquier toque/clic en curso.
      clearLongPress();
      startClient = null;
      activeTouchId = null;
      twoFingerLastY = midpointY(event.touches);
      return;
    }

    const touch = event.changedTouches[0];
    if (touch === undefined) return;
    activeTouchId = touch.identifier;
    lastClient = { x: touch.clientX, y: touch.clientY };
    startClient = { x: touch.clientX, y: touch.clientY, t: performance.now() };
    moved = false;

    // Mantener pulsado sin mover → clic derecho en la posición actual del cursor.
    clearLongPress();
    longPressTimer = window.setTimeout(() => {
      if (moved || startClient === null) return;
      send({ t: 'down', b: 2, x: normX, y: normY });
      send({ t: 'up', b: 2, x: normX, y: normY });
      startClient = null;
    }, LONG_PRESS_MS);
  };

  const findTouch = (touches: TouchList): Touch | null => {
    if (activeTouchId === null) return null;
    for (let i = 0; i < touches.length; i += 1) {
      const touch = touches[i];
      if (touch !== undefined && touch.identifier === activeTouchId) return touch;
    }
    return null;
  };

  const onTouchMove = (event: TouchEvent): void => {
    if (!touchActive) return;
    event.preventDefault();

    if (event.touches.length >= 2) {
      const midY = midpointY(event.touches);
      if (midY !== null && twoFingerLastY !== null) {
        // Delta de dedos como deltaY estilo DOM; el helper ya invierte el signo.
        send({ t: 'wheel', dx: 0, dy: (twoFingerLastY - midY) * 2 });
      }
      twoFingerLastY = midY;
      return;
    }

    const touch = findTouch(event.touches);
    if (touch === null || lastClient === null) return;

    const rect = video.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    // Movimiento RELATIVO (trackpad): el cursor se mueve según lo que arrastra el
    // dedo, escalado por la velocidad. No importa el object-fit ni las franjas.
    const dx = touch.clientX - lastClient.x;
    const dy = touch.clientY - lastClient.y;
    lastClient = { x: touch.clientX, y: touch.clientY };

    normX = clamp(normX + (dx / rect.width) * TRACKPAD_SPEED);
    normY = clamp(normY + (dy / rect.height) * TRACKPAD_SPEED);

    if (
      startClient !== null &&
      Math.hypot(touch.clientX - startClient.x, touch.clientY - startClient.y) > TAP_MOVE_THRESHOLD
    ) {
      moved = true;
      clearLongPress();
    }

    send({ t: 'move', x: normX, y: normY });
  };

  const onTouchEnd = (event: TouchEvent): void => {
    if (!touchActive) return;
    event.preventDefault();
    clearLongPress();
    if (event.touches.length === 0) twoFingerLastY = null;

    // Toque corto y quieto → clic izquierdo donde esté el cursor (no donde se tocó:
    // el usuario ya lo posicionó arrastrando). Sin problema de oclusión del dedo.
    if (startClient !== null && !moved && performance.now() - startClient.t < TAP_MAX_MS) {
      send({ t: 'down', b: 0, x: normX, y: normY });
      send({ t: 'up', b: 0, x: normX, y: normY });
    }
    startClient = null;
    activeTouchId = null;
    lastClient = null;
  };

  const onBeforeInput = (event: InputEvent): void => {
    if (!touchActive) return;
    if (event.inputType === 'insertText' && event.data !== null) {
      send({ t: 'text', text: event.data });
    } else if (event.inputType === 'insertLineBreak') {
      send({ t: 'key', code: 'Enter', down: true, mods: [] });
      send({ t: 'key', code: 'Enter', down: false, mods: [] });
    } else if (event.inputType === 'deleteContentBackward') {
      send({ t: 'key', code: 'Backspace', down: true, mods: [] });
      send({ t: 'key', code: 'Backspace', down: false, mods: [] });
    }
    event.preventDefault();
    keyboardInput.value = '';
  };

  document.addEventListener('mousemove', onMouseMove);
  document.addEventListener('mousedown', onMouseDown);
  document.addEventListener('mouseup', onMouseUp);
  document.addEventListener('wheel', onWheel, { passive: false });
  document.addEventListener('contextmenu', onContextMenu);
  document.addEventListener('keydown', onKeyDown);
  document.addEventListener('keyup', onKeyUp);
  document.addEventListener('pointerlockchange', onPointerLockChange);
  video.addEventListener('click', onVideoClick);
  video.addEventListener('touchstart', onTouchStart, { passive: false });
  video.addEventListener('touchmove', onTouchMove, { passive: false });
  video.addEventListener('touchend', onTouchEnd, { passive: false });
  video.addEventListener('touchcancel', onTouchEnd, { passive: false });
  keyboardInput.addEventListener('beforeinput', onBeforeInput);

  return {
    touchMode,

    async engage(): Promise<void> {
      // Pantalla completa (real en Android/desktop, CSS en iOS) para tener toda la
      // superficie de control. Funciona igual si el usuario la rechaza.
      await enterFullscreen({ element: video, container: videoWrap }).catch(() => undefined);

      if (touchMode) {
        touchActive = true;
        onActiveChange(true);
        log('control táctil activo: arrastra para mover, toca para clic, mantén para clic derecho, dos dedos para desplazar.');
        return;
      }

      const keyboard = (navigator as Navigator & { keyboard?: KeyboardLockCapable }).keyboard;
      if (keyboard?.lock !== undefined) {
        await keyboard.lock().catch((error: unknown) => {
          log(`Keyboard Lock no disponible — ${describe(error)} (Cmd+W/Cmd+Tab no se capturarán)`);
        });
      } else {
        log('este navegador no soporta Keyboard Lock; Cmd+W y Cmd+Tab no se capturarán');
      }

      await video.requestPointerLock().catch((error: unknown) => {
        log(`no se pudo capturar el puntero — ${describe(error)}`);
      });
    },

    release(): void {
      touchActive = false;
      clearLongPress();
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('wheel', onWheel);
      document.removeEventListener('contextmenu', onContextMenu);
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('keyup', onKeyUp);
      document.removeEventListener('pointerlockchange', onPointerLockChange);
      video.removeEventListener('click', onVideoClick);
      video.removeEventListener('touchstart', onTouchStart);
      video.removeEventListener('touchmove', onTouchMove);
      video.removeEventListener('touchend', onTouchEnd);
      video.removeEventListener('touchcancel', onTouchEnd);
      keyboardInput.removeEventListener('beforeinput', onBeforeInput);

      const keyboard = (navigator as Navigator & { keyboard?: KeyboardLockCapable }).keyboard;
      keyboard?.unlock?.();
      if (document.pointerLockElement === video) document.exitPointerLock();
      void exitFullscreen({ element: video, container: videoWrap });
      onActiveChange(false);
    },
  };
}

function describe(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
